import React, { useEffect, useState } from 'react'
import { IoMdArrowDropleft, IoMdArrowDropright } from 'react-icons/io'
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md'
import styled from 'styled-components'
import { layout, LayoutProps, position, PositionProps, variant } from 'styled-system'
import { setInterval } from 'timers'

type ItemProps = {
	img: string
	news: NewsFeed
}
/**
 * Matches `theme.mediaQueries.small`. Kept as a literal because <source media>
 * is evaluated by the browser, not styled-components, so it cannot read theme.
 */
const MOBILE_MEDIA = '(max-width: 769px)'

const SwiperItem: React.FC<ItemProps> = ({ img, news }) => {
	const openLink = () => {
		window.open(news.redirection_url, '_blank')
	}
	/**
	 * Admin crops two files per feed item — 58:11 for desktop and 72:43 for
	 * mobile. Only `photo` was ever rendered, so phones got the desktop crop and
	 * object-fit: cover threw away roughly 70% of its width. `photo_sm` is
	 * nullable (it postdates the table), hence the guard: rows without one fall
	 * back to the desktop file exactly as before.
	 */
	return (
		<SwiperImageWrapper onClick={openLink}>
			<picture>
				{news.photo_sm && <source media={MOBILE_MEDIA} srcSet={news.photo_sm} />}
				<img alt={news.content} src={img} />
			</picture>
		</SwiperImageWrapper>
	)
}
interface ImagesSwiperComposition {
	Item: React.FC<ItemProps>
}

type Props = {
	children: React.ReactElement<ItemProps>[] | React.ReactElement<ItemProps> | undefined
} & LayoutProps

const ImagesSwiper: React.FC<Props> & ImagesSwiperComposition = ({ children, ...props }) => {
	const [currentSlide, setCurrentSlide] = useState(0)

	useEffect(() => {
		let intervalID: NodeJS.Timeout
		const count = React.Children.count(children)
		if (count > 1) {
			intervalID = setTimeout(() => {
				const newIndex = currentSlide === count - 1 ? 0 : currentSlide + 1
				setCurrentSlide(newIndex)
			}, 5000)
		}
		return () => clearInterval(intervalID)
	}, [children, currentSlide])

	const handleNext = () => {
		const count = React.Children.count(children)
		setCurrentSlide(currentSlide === count - 1 ? 0 : currentSlide + 1)
	}
	const handlePrev = () => {
		const count = React.Children.count(children)
		setCurrentSlide(currentSlide === 0 ? count - 1 : currentSlide - 1)
	}

	// The dots already run right-to-left in Arabic, so the controls mirror with
	// them: back sits on the right and points right, next on the left.
	const isRtl = document.documentElement.dir === 'rtl'

	return (
		<SwiperContentWrapper {...props}>
			<SwiperButton onClick={handlePrev} {...(isRtl ? { right: '0' } : { left: '0' })}>
				{isRtl ? <MdArrowForwardIos /> : <MdArrowBackIosNew />}
			</SwiperButton>
			<SwiperInnerContent>
				{React.Children.map(children, (child, index) => {
					if (currentSlide === index) {
						return child
					}
				})}
				<SwiperDotsContainer>
					{React.Children.map(children, (child, index: number) => {
						return <Dot $selected={currentSlide === index} />
					})}
				</SwiperDotsContainer>
			</SwiperInnerContent>
			<SwiperButton onClick={handleNext} {...(isRtl ? { left: '0' } : { right: '0' })}>
				{isRtl ? <MdArrowBackIosNew /> : <MdArrowForwardIos />}
			</SwiperButton>
		</SwiperContentWrapper>
	)
}

ImagesSwiper.Item = SwiperItem

/**
 * The box holds the ratio the admin cropper enforces on each file, so
 * object-fit: cover never has anything left to crop: 72:43 while the
 * <picture> serves photo_sm, 58:11 once it swaps to the desktop file. The
 * switch must mirror MOBILE_MEDIA exactly (770 = 769 + 1) — pinning it to the
 * theme's 1024/1201 desktop lines would re-crop the band in between. The vh
 * heights this replaces could never line up: a viewport-shaped box only
 * matches a ratio-locked image by coincidence.
 */
const SwiperContentWrapper = styled.div<LayoutProps>`
	position: relative;
	width: 100%;
	aspect-ratio: 72 / 43;
	@media (min-width: 770px) {
		aspect-ratio: 58 / 11;
	}
	img {
		max-width: 100%;
	}
	${layout}
`
const SwiperInnerContent = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`

const SwiperImageWrapper = styled.div`
	cursor: pointer;
	height: 100%;
	picture {
		display: block;
		width: 100%;
		height: 100%;
	}
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`
const SwiperButton = styled.div<PositionProps>`
	margin: 0.813rem 0.875rem;
	font-size: 1rem;
	width: 30px;
	height: 30px;
	background: ${props => props.theme.colors.purple};
	border-radius: 50%;
	display: grid;
	place-content: center;
	svg {
		direction: ltr;
		transform: unset !important;
		/* opacity: 0.5; */
	}
	opacity: 0.5;
	position: absolute;
	color: #fff;

	bottom: 0%;
	z-index: 5;
	${position}
	cursor: pointer;
	transition: all ease-in-out 100ms;
	:hover {
		opacity: 1;
	}

	${({ theme }) => `${theme.mediaQueries.large}{
		font-size: 1.2rem; 
		width: 40px;
		height: 40px;
		margin: 1.813rem 2.875rem;
	}`}
`

const Dot = styled.div<{ $selected: boolean }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${props => (props.$selected ? props.theme.colors.purple : '#FFF')};
`
const SwiperDotsContainer = styled.div`
	position: absolute;
	bottom: 0;
	width: 100%;
	background: linear-gradient(transparent, #0000004b);
	display: flex;
	grid-gap: 8px;
	justify-content: center;
	padding-bottom: 1.563rem;
	padding-top: 2rem;
`
export default ImagesSwiper
